import { useState, useCallback } from 'react';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';

function analyzeMeta(title, desc, url) {
  var issues = [];
  var good = [];
  if (!title.trim()) issues.push('Missing page title');
  else if (title.length < 30) issues.push('Title is too short (' + title.length + ' chars). Aim for 50-60.');
  else if (title.length > 60) issues.push('Title is too long (' + title.length + ' chars). Keep under 60.');
  else good.push('Title length is good (' + title.length + ' chars)');

  if (!desc.trim()) issues.push('Missing meta description');
  else if (desc.length < 120) issues.push('Description is too short (' + desc.length + ' chars). Aim for 150-160.');
  else if (desc.length > 160) issues.push('Description is too long (' + desc.length + ' chars). Keep under 160.');
  else good.push('Description length is good (' + desc.length + ' chars)');

  if (url.trim()) {
    if (url.length > 75) issues.push('URL is quite long (' + url.length + ' chars). Shorter URLs rank better.');
    else good.push('URL length is good');
    if (/[A-Z]/.test(url)) issues.push('URL contains uppercase letters. Use lowercase for consistency.');
    if (/\s/.test(url)) issues.push('URL contains spaces. Use hyphens instead.');
    if (!/^https?:\/\//.test(url) && url.length > 0) issues.push('URL should start with https://');
  }

  if (title && desc) {
    var titleWords = title.toLowerCase().split(/\s+/);
    var descLower = desc.toLowerCase();
    var overlap = titleWords.filter(function (w) { return w.length > 3 && descLower.includes(w); });
    if (overlap.length > 0) good.push('Title keywords found in description: ' + overlap.join(', '));
    else issues.push('Consider including title keywords in your description for better SEO.');
  }

  return { issues: issues, good: good, score: Math.round((good.length / (good.length + issues.length)) * 100) || 0 };
}

function SeoChecker() {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [url, setUrl] = useState('');
  const [result, setResult] = useState(null);

  var check = useCallback(function () {
    setResult(analyzeMeta(title, desc, url));
  }, [title, desc, url]);

  return (
    <div className="page-prettifier">
      <SEO title="SEO Meta Tag Checker - Analyze Title & Description" description="Free online SEO meta tag checker. Analyze your page title, meta description, and URL for search engine optimization. Get actionable suggestions." path="/seo-checker" />
      <div className="container">
        <div className="tool-header">
          <h1>SEO Meta Tag Checker</h1>
          <p>Check if your page title, meta description, and URL are optimized for search engines.</p>
        </div>
        <AdUnit />
        <div className="calc-inputs" style={{ maxWidth: '700px' }}>
          <div className="form-group">
            <label htmlFor="seo-title">Page Title <span className="char-count">({title.length}/60)</span></label>
            <input id="seo-title" type="text" value={title} onChange={function (e) { setTitle(e.target.value); }} className="color-input" placeholder="Your page title..." />
          </div>
          <div className="form-group">
            <label htmlFor="seo-desc">Meta Description <span className="char-count">({desc.length}/160)</span></label>
            <textarea id="seo-desc" value={desc} onChange={function (e) { setDesc(e.target.value); }} className="color-input" rows={3} placeholder="Your meta description..." style={{ resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          <div className="form-group">
            <label htmlFor="seo-url">Page URL</label>
            <input id="seo-url" type="text" value={url} onChange={function (e) { setUrl(e.target.value); }} className="color-input" placeholder="https://example.com/your-page" />
          </div>
          <button onClick={check} className="btn btn-primary" type="button">Analyze</button>
        </div>

        {result && (
          <div className="seo-results">
            <div className="seo-score">
              <div className="seo-score-number" style={{ color: result.score >= 70 ? 'var(--accent)' : result.score >= 40 ? '#ffa94d' : 'var(--error)' }}>{result.score}%</div>
              <div className="stat-label">SEO Score</div>
            </div>
            {result.issues.length > 0 && (
              <div className="seo-section">
                <h3>Issues to Fix</h3>
                <ul className="seo-list seo-issues">{result.issues.map(function (item, i) { return <li key={i}>{item}</li>; })}</ul>
              </div>
            )}
            {result.good.length > 0 && (
              <div className="seo-section">
                <h3>Looking Good</h3>
                <ul className="seo-list seo-good">{result.good.map(function (item, i) { return <li key={i}>{item}</li>; })}</ul>
              </div>
            )}
          </div>
        )}

        <section className="tool-guide">
          <h2>Why Meta Tags Matter for SEO</h2>
          <p>Your page title and meta description are the first things people see in search results. A well-crafted title improves click-through rates, and a compelling description convinces users to visit your page. Search engines also use these tags to understand what your page is about, which affects your ranking.</p>
          <h2>Best Practices</h2>
          <ul>
            <li>Keep titles between 50-60 characters to avoid truncation in search results.</li>
            <li>Keep descriptions between 150-160 characters for optimal display.</li>
            <li>Include your primary keyword near the beginning of the title.</li>
            <li>Write unique titles and descriptions for every page.</li>
            <li>Use action-oriented language in descriptions to encourage clicks.</li>
            <li>Keep URLs short, lowercase, and use hyphens between words.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default SeoChecker;
